// src/router/tenantRouting.ts
import type { Router, RouteLocationRaw } from 'vue-router';
import { useTenantStore } from '../stores/useTenantStore';
import { useMedicalRequestStore } from '../stores/useMedicalRequestStore';
import { useAdminRequestStore } from '../stores/useAdminRequestStore';
import { useRepeatPrescriptionRequestStore } from '../stores/useRepeatPrescriptionRequestStore';

const TENANT_REGEX = /^[A-Fa-f0-9]{6}$/;

// ** IMPORTANT **
// we modify all .push() calls to add the tenant prefix if needed.

export function patchTenantAwarePush(router: Router) {
  const originalPush = router.push.bind(router);

  router.push = function patchedPush(location: RouteLocationRaw) {
    let path: string | null = null;

    // Only touch-path-based navigations.
    if (typeof location === 'string') {
      path = location;
    } else if ('path' in location && location.path) {
      path = location.path;
    } else {
      // e.g. { name: 'error' } → leave alone
      return originalPush(location);
    }

    if (!path.startsWith('/')) {
      path = '/' + path;
    }

    const segments = path.split('/').filter(Boolean);
    const first = segments[0] ?? '';

    // Already has tenant prefix? Do nothing.
    if (TENANT_REGEX.test(first)) {
      return originalPush(location);
    }

    const tenantStore = useTenantStore();
    const tenantId = tenantStore.tenant?.tenantId;

    if (!tenantId) {
      return originalPush(location);
    }

    const newPath = '/' + [tenantId, ...segments].join('/');

    const newLocation: RouteLocationRaw =
      typeof location === 'string' ? newPath : { ...location, path: newPath };

    return originalPush(newLocation);
  } as typeof router.push;
}

/**
 * Register the beforeEach guards that depend on tenant + stores.
 */
export function registerTenantGuards(router: Router) {
  router.beforeEach(async (to) => {
    const title = to.meta.pageTitle || to.meta.layoutPageTitle || '';
    document.title = `Patient Portal ${title ? `| ${title}` : ''}`;

    const medicalRequestStore = useMedicalRequestStore();
    const adminRequestStore = useAdminRequestStore();
    const repeatPrescriptionRequestStore = useRepeatPrescriptionRequestStore();
    const tenantStore = useTenantStore();

    const segments = to.path.split('/').filter(Boolean);
    const tenantId = segments[0] ?? null;
    const firstUrlSegment = segments[1] ?? null;

    let store:
      | ReturnType<typeof useMedicalRequestStore>
      | ReturnType<typeof useAdminRequestStore>
      | ReturnType<typeof useRepeatPrescriptionRequestStore>
      | null = null;

    if (firstUrlSegment === 'medical-request') store = medicalRequestStore;
    else if (firstUrlSegment === 'admin-request') store = adminRequestStore;
    else if (firstUrlSegment === 'repeat-prescription-request')
      store = repeatPrescriptionRequestStore;

    if (to.meta.requiresLoadedTenant && !tenantStore.isLoaded) {
      console.error('tenant not loaded yet');
      return tenantId ? `/${tenantId}` : '/';
    }

    if (to.meta.requiresValidPatient && !store?.isValidPatient) {
      console.error('isValidPatient not valid');
      return tenantId ? `/${tenantId}` : '/';
    }

    if (to.meta.requiresVerifiedPatient && !store?.isVerified) {
      console.error('requiresVerifiedPatient not valid');
      return tenantId ? `/${tenantId}` : '/';
    }

    return true; // allow navigation
  });
}
